import { toError } from "@gaming/data-validation";
import type { ErrorInfo, ReactNode } from "react";
import React, { useRef } from "react";
import { ErrorPage, ErrorPagePresentational } from "../ErrorPage";

type ErrorBoundaryState = Readonly<{
  error?: Error;
}>;

type ErrorBoundaryProps = {
  children: () => ReactNode;
  renderError?: (error: Error) => JSX.Element | null;
};

type ErrorBoundaryInternalProps = ErrorBoundaryProps &
  Readonly<{
    renderError: (error: Error) => JSX.Element | null;
  }>;

type ServerErrorBoundaryProps = ErrorBoundaryInternalProps;

type ClientErrorBoundaryProps = Omit<ErrorBoundaryInternalProps, "children"> & {
  children: ReactNode | (() => ReactNode);
};

type ChildrenRendererProps = Readonly<{
  renderChildren: () => ReactNode;
}>;

// this is needed to be able to capture the error correctly within the
// ClientErrorBoundary, or else it would be propagated upwards
export const ChildrenRenderer = ({ renderChildren }: ChildrenRendererProps) => (
  <>{renderChildren()}</>
);

const isServerRender = () => typeof window === "undefined";

class ClientErrorBoundary extends React.Component<ClientErrorBoundaryProps, ErrorBoundaryState> {
  public constructor(props: ErrorBoundaryInternalProps) {
    super(props);
    this.state = {};
  }

  public static getDerivedStateFromError(error: Error) {
    return { error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorMessage = `Error while rendering content. \n OriginalMessage: ${error.message} \n ComponentStack: ${errorInfo.componentStack}`;
    console.error(errorMessage);
  }

  public render() {
    return this.state.error ? this.props.renderError(this.state.error) : <>{this.props.children}</>;
  }
}

const ServerErrorBoundary = (props: ServerErrorBoundaryProps) => {
  const error = useRef<Error | undefined>(undefined);

  let renderedChildren: ReactNode = null;
  if (!error.current) {
    try {
      renderedChildren = props.children();
    } catch (e) {
      const err = toError(e);
      error.current = err;
      const errorMessage = `Error while rendering content. \n OriginalMessage: ${err.message}. StackTrace: ${err.stack}`;
      console.error(errorMessage);
    }
  }

  return error.current ? <ErrorPage error={error.current} /> : <>{renderedChildren}</>;
};

const ErrorBoundaryInternal = ({ children, ...otherProps }: ErrorBoundaryInternalProps) =>
  isServerRender() ? (
    <ServerErrorBoundary {...otherProps}>{children}</ServerErrorBoundary>
  ) : (
    <ClientErrorBoundary {...otherProps}>
      <ChildrenRenderer renderChildren={children} />
    </ClientErrorBoundary>
  );

export const ErrorBoundary = (props: ErrorBoundaryProps) => {
  const renderError = props.renderError || ((error: Error) => <ErrorPage error={error} />);
  return <ErrorBoundaryInternal {...props} renderError={renderError} />;
};

/**
 * Critical Error Boundary to be rendered as last line of defense when all else
 * has failed including the first Error Boundary. This is configured outside all
 * context, so would not have access to any context or redux, etc.., and should
 * not use anything as such
 */
export const CriticalErrorBoundary = (props: ErrorBoundaryProps & { cV: string }) => {
  const title = "We are sorry, the page you requested cannot be found.";
  const subTitle =
    "The URL may be misspelled or the page you're looking for is no longer available.";
  const errorCodeDisplayString = `Error code: ${props.cV}`;
  const renderError =
    props.renderError ||
    ((error: Error) => (
      <ErrorPagePresentational
        title={title}
        error={error}
        errorCodeDisplayString={errorCodeDisplayString}
        subtitle={subTitle}
      />
    ));

  return <ErrorBoundaryInternal {...props} renderError={renderError} />;
};
